import { createEntityAdapter, createSelector, createSlice, EntityId } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { DateFormat } from 'enums/dateFormats';
import { Status } from 'enums/messages';
import groupBy from 'lodash/groupBy';
import type { RootState } from 'store';

import { formatSMSs } from './sms.settigns';
import { SMSProps } from './sms.types';
import { smsApiSlice } from './smsApi';

const smsAdapter = createEntityAdapter<SMSProps, EntityId>({
  selectId: (sms) => sms._id,
  sortComparer: (a, b) => a.createdAt?.localeCompare(b.createdAt)
});

const initialState = { SMSs: smsAdapter.getInitialState(), status: Status.Idle, totalCount: 0 };

let lastRequestId: string;

const smsSlice = createSlice({
  extraReducers: (builder) => {
    builder.addMatcher(smsApiSlice.endpoints.getSMS.matchPending, (state, action) => {
      lastRequestId = action.meta.requestId;
      state.status = Status.Pending;
    });
    builder.addMatcher(smsApiSlice.endpoints.getSMS.matchFulfilled, (state, { meta, payload }) => {
      if (lastRequestId === meta.requestId) {
        smsAdapter.upsertMany(state.SMSs, payload.smsArray);
        state.totalCount = payload.totalCount;
        state.status = Status.Fulfilled;
      } else {
        state = initialState;
      }
    });
    builder.addMatcher(smsApiSlice.endpoints.getSMS.matchRejected, (state) => {
      state.status = Status.Rejected;
    });
    builder.addMatcher(smsApiSlice.endpoints.sendSMS.matchFulfilled, (state, { payload }) => {
      const newMessage = {
        _id: payload._id,
        createdAt: payload.createdAt,
        message: payload.message,
        senderDetails: payload.receiverDetails,
        status: payload.status
      };
      smsAdapter.upsertOne(state.SMSs, newMessage as SMSProps);
      state.status = Status.Fulfilled;
    });
    builder.addMatcher(smsApiSlice.endpoints.sendSMS.matchRejected, (state) => {
      state.status = Status.Rejected;
    });
  },
  initialState,
  name: 'sms',
  reducers: {
    resetSMSs: () => initialState
  }
});

export const smsSelectors = smsAdapter.getSelectors<RootState>((state) => state.sms.SMSs);

export const selectSMS = (state: RootState) => state.sms;

export const selectFormattedSMSs = createSelector(smsSelectors.selectAll, (SMSs) => {
  const formattedSMSs = formatSMSs(SMSs);
  return groupBy(formattedSMSs, (sms) => dayjs(sms.date).format(DateFormat.MM_DD_YYYY));
});

export const selectLatestSMS = createSelector(smsSelectors.selectAll, (SMSs) => {
  return SMSs[SMSs.length - 1];
});

export const { resetSMSs } = smsSlice.actions;

export default smsSlice.reducer;
